/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/getRecipClassifiedROIContours.js
 *
 * RECIP 1.0 v5 (error-fix) — liverROINames fallback to built-in defaults.
 *
 * Change from D-2 version:
 *   When criteria.liverROINames is absent or empty (existing saved studies
 *   that predate the liverROINames field, or studies using a criteria object
 *   that doesn't yet have a Liver Ref ROI Names entry in the settings UI),
 *   fall back to DEFAULT_LIVER_NAMES from getRecipThresholds.js:
 *     ['right liver lobe', 'liver reference', 'liver', 'right hepatic lobe']
 *
 *   This ensures "Liver Reference ROI" (and similar common names) are
 *   auto-classified as 'liver-reference' without requiring the reader to
 *   set the liverROINames field in Criteria settings.
 *
 *   Note: criteria.liverROINames, when set, still takes precedence over
 *   the defaults, so clinical sites that need custom patterns can override.
 *
 * NOTE FOR THE SETTINGS UI:
 *   To expose liverROINames in the Criteria settings panel (alongside the
 *   existing bloodPoolROINames field), add a "Liver Ref ROI Names" field
 *   to the RECIP criteria form. Share the criteria settings component file
 *   and it can be added in the same style as bloodPoolROINames.
 */
import _ from 'lodash';
import { TRACKING_TYPES } from './guideline';
import { DEFAULT_LIVER_NAMES } from './getRecipThresholds';

const LEGACY_MIGRATION = {
  [TRACKING_TYPES.TARGET]: TRACKING_TYPES.LESION,
  [TRACKING_TYPES.PSMA_NEGATIVE]: TRACKING_TYPES.LESION,
  [TRACKING_TYPES.NEW_PSMA_POSITIVE]: TRACKING_TYPES.NEW_LESION,
  [TRACKING_TYPES.NEW_PSMA_NEGATIVE]: TRACKING_TYPES.NEW_LESION,
  [TRACKING_TYPES.NEW]: TRACKING_TYPES.NEW_LESION,
  [TRACKING_TYPES.NEW_TARGET]: TRACKING_TYPES.NEW_LESION,
  [TRACKING_TYPES.NEW_NONTARGET]: TRACKING_TYPES.NEW_LESION,
  [TRACKING_TYPES.NOTINCLUDED]: TRACKING_TYPES.OTHER,
  [TRACKING_TYPES.NONTARGET]: TRACKING_TYPES.LESION,
};

const STICKY_TYPES = new Set([
  TRACKING_TYPES.BLOOD_POOL,
  TRACKING_TYPES.LIVER_REFERENCE,
  TRACKING_TYPES.OTHER,
]);

function hasMeasurementAt(timepoint, roiNumber) {
  const lesion = timepoint?.Lesions?.find(l => l.ROINumber === roiNumber);
  if (!lesion) return false;
  const area = lesion.maxAreaValue || 0;
  const vol = lesion.volume || lesion.Volume || 0;
  const diameter = lesion.maxDiameterValue || 0;
  return area > 0 || vol > 0 || diameter > 0;
}

function hasMeasurementAtAnyFollowup(timepoints, roiNumber) {
  for (let i = 1; i < timepoints.length; i++) {
    if (hasMeasurementAt(timepoints[i], roiNumber)) return true;
  }
  return false;
}

function matchesNamePattern(roiName, patterns) {
  if (!roiName || !patterns || !patterns.length) return false;
  const lower = String(roiName).toLowerCase();
  return patterns.some(p => {
    const needle = String(p || '')
      .trim()
      .toLowerCase();
    return needle.length > 0 && lower.includes(needle);
  });
}

function getRecipClassifiedROIContours(
  timepoints,
  ROIContours,
  criteria,
  forceUpdate = true
) {
  const cloned = _.cloneDeep(ROIContours);

  const bpPatterns = Array.isArray(criteria?.bloodPoolROINames)
    ? criteria.bloodPoolROINames
    : [];

  // Use criteria.liverROINames if set and non-empty; otherwise fall back to
  // built-in defaults so existing studies without this setting still work.
  const liverPatterns =
    Array.isArray(criteria?.liverROINames) && criteria.liverROINames.length > 0
      ? criteria.liverROINames
      : DEFAULT_LIVER_NAMES;

  const out = cloned
    .map(roi => {
      const currentType = roi.trackingType;
      const hasBaseline = hasMeasurementAt(timepoints[0], roi.ROINumber);
      const hasFollowup = hasMeasurementAtAnyFollowup(
        timepoints,
        roi.ROINumber
      );

      /* -------- (0a) Blood-pool name auto-classification -------- */
      if (
        currentType !== TRACKING_TYPES.OTHER &&
        matchesNamePattern(roi.ROIName, bpPatterns)
      ) {
        roi.trackingType = TRACKING_TYPES.BLOOD_POOL;
        roi.measurable = false;
        return roi;
      }

      /* -------- (0b) Liver-reference name auto-classification -------- */
      if (
        currentType !== TRACKING_TYPES.OTHER &&
        currentType !== TRACKING_TYPES.BLOOD_POOL &&
        matchesNamePattern(roi.ROIName, liverPatterns)
      ) {
        roi.trackingType = TRACKING_TYPES.LIVER_REFERENCE;
        roi.measurable = false;
        return roi;
      }

      /* -------- (1) Sticky types — preserve -------- */
      if (STICKY_TYPES.has(currentType)) {
        roi.measurable = false;
        return roi;
      }

      /* -------- (2) Migrate legacy v3 types -------- */
      if (LEGACY_MIGRATION[currentType]) {
        const migrated = LEGACY_MIGRATION[currentType];
        roi.trackingType = migrated;
        if (migrated === TRACKING_TYPES.LESION && !hasBaseline) {
          if (hasFollowup) {
            roi.trackingType = TRACKING_TYPES.NEW_LESION;
          } else {
            roi.trackingType = TRACKING_TYPES.OTHER;
            roi.measurable = false;
            return roi;
          }
        } else if (migrated === TRACKING_TYPES.NEW_LESION && hasBaseline) {
          roi.trackingType = TRACKING_TYPES.LESION;
        } else if (migrated === TRACKING_TYPES.NEW_LESION && !hasFollowup) {
          roi.trackingType = TRACKING_TYPES.OTHER;
          roi.measurable = false;
          return roi;
        }
        roi.measurable =
          roi.trackingType === TRACKING_TYPES.LESION ||
          roi.trackingType === TRACKING_TYPES.NEW_LESION;
        return roi;
      }

      /* -------- (3) Already-classified v5 lesion / new-lesion -------- */
      const isUnclassified =
        !currentType ||
        currentType === TRACKING_TYPES.NONE ||
        currentType === TRACKING_TYPES.ISOLATED;

      const needsRecheck =
        forceUpdate &&
        (currentType === TRACKING_TYPES.LESION ||
          currentType === TRACKING_TYPES.NEW_LESION);

      if (isUnclassified || needsRecheck) {
        if (hasBaseline) {
          roi.trackingType = TRACKING_TYPES.LESION;
          roi.measurable = true;
        } else if (hasFollowup) {
          roi.trackingType = TRACKING_TYPES.NEW_LESION;
          roi.measurable = true;
        } else {
          roi.trackingType = TRACKING_TYPES.OTHER;
          roi.measurable = false;
        }
        return roi;
      }

      /* -------- (4) Fall-through -------- */
      roi.trackingType = TRACKING_TYPES.OTHER;
      roi.measurable = false;
      return roi;
    })
    .sort((a, b) => Number(a.ROINumber) - Number(b.ROINumber));

  /* Sanity warnings */
  const bpCount = out.filter(r => r.trackingType === TRACKING_TYPES.BLOOD_POOL)
    .length;
  if (bpCount > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[RECIP] ${bpCount} blood-pool ROIs detected. blood-pool is decorative under RECIP 1.0.`,
      out
        .filter(r => r.trackingType === TRACKING_TYPES.BLOOD_POOL)
        .map(r => ({ ROINumber: r.ROINumber, ROIName: r.ROIName }))
    );
  }
  const liverCount = out.filter(
    r => r.trackingType === TRACKING_TYPES.LIVER_REFERENCE
  ).length;
  if (liverCount > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[RECIP] ${liverCount} liver-reference ROIs detected. Using first for threshold computation.`,
      out
        .filter(r => r.trackingType === TRACKING_TYPES.LIVER_REFERENCE)
        .map(r => ({ ROINumber: r.ROINumber, ROIName: r.ROIName }))
    );
  }

  return out;
}

export default getRecipClassifiedROIContours;
